import { DependencyType, FlattenedTask, TimelineConfig, DependencyPathData, ROW_HEIGHT, BAR_HEIGHT, BAR_PADDING } from '../types/gantt.types';
import { dateToPixel } from './dateUtils';

/**
 * Get the attachment point on a task bar
 */
function getTaskAttachmentPoint(
  task: FlattenedTask,
  side: 'start' | 'end',
  config: TimelineConfig
): { x: number; y: number } {
  const barY = task.rowIndex * config.rowHeight + BAR_PADDING + BAR_HEIGHT / 2;

  if (side === 'start') {
    const x = dateToPixel(task.startDate, config.startDate, config.dayWidth);
    return { x, y: barY };
  } else {
    const x = dateToPixel(task.endDate, config.startDate, config.dayWidth);
    return { x, y: barY };
  }
}

/**
 * Calculate SVG path for dependency connector with smooth curves
 */
export function calculateDependencyPath(
  fromTask: FlattenedTask,
  toTask: FlattenedTask,
  type: DependencyType,
  config: TimelineConfig
): DependencyPathData {
  // Determine attachment points based on dependency type
  const fromSide = type === 'SS' || type === 'SF' ? 'start' : 'end';
  const toSide = type === 'FS' || type === 'SS' ? 'start' : 'end';

  const from = getTaskAttachmentPoint(fromTask, fromSide, config);
  const to = getTaskAttachmentPoint(toTask, toSide, config);

  // Build path
  const path = buildPath(from, to, fromSide, toSide, config.rowHeight);

  // Calculate arrow head
  const arrowPoints = calculateArrowHead(to, toSide);

  // Calculate midpoint for lag label
  const midPoint = {
    x: (from.x + to.x) / 2,
    y: (from.y + to.y) / 2,
  };

  return { path, arrowPoints, midPoint };
}

/**
 * Build SVG path string with smooth curves
 */
function buildPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromSide: 'start' | 'end',
  toSide: 'start' | 'end',
  rowHeight: number
): string {
  const horizontalOffset = 15;
  const cornerRadius = 6;

  // Same row - simple horizontal
  if (Math.abs(from.y - to.y) < 5) {
    if (from.x < to.x) {
      return `M ${from.x} ${from.y} L ${to.x - 6} ${to.y}`;
    } else {
      // Need to loop around
      const loopY = from.y + rowHeight * 0.6;
      return `
        M ${from.x} ${from.y}
        L ${from.x + horizontalOffset} ${from.y}
        Q ${from.x + horizontalOffset + cornerRadius} ${from.y} ${from.x + horizontalOffset + cornerRadius} ${from.y + cornerRadius}
        L ${from.x + horizontalOffset + cornerRadius} ${loopY - cornerRadius}
        Q ${from.x + horizontalOffset + cornerRadius} ${loopY} ${from.x + horizontalOffset} ${loopY}
        L ${to.x - horizontalOffset} ${loopY}
        Q ${to.x - horizontalOffset - cornerRadius} ${loopY} ${to.x - horizontalOffset - cornerRadius} ${loopY - cornerRadius}
        L ${to.x - horizontalOffset - cornerRadius} ${to.y + cornerRadius}
        Q ${to.x - horizontalOffset - cornerRadius} ${to.y} ${to.x - horizontalOffset} ${to.y}
        L ${to.x - 6} ${to.y}
      `.replace(/\s+/g, ' ').trim();
    }
  }

  const isDownward = to.y > from.y;
  const isForward = to.x > from.x;

  // Standard step path
  if (fromSide === 'end' && toSide === 'start') {
    if (isForward) {
      // Simple step down/up
      const midX = from.x + (to.x - from.x) / 2;

      return `
        M ${from.x} ${from.y}
        L ${midX - cornerRadius} ${from.y}
        Q ${midX} ${from.y} ${midX} ${from.y + (isDownward ? cornerRadius : -cornerRadius)}
        L ${midX} ${to.y + (isDownward ? -cornerRadius : cornerRadius)}
        Q ${midX} ${to.y} ${midX + cornerRadius} ${to.y}
        L ${to.x - 6} ${to.y}
      `.replace(/\s+/g, ' ').trim();
    } else {
      // Need to go around
      const exitX = from.x + horizontalOffset;
      const entryX = to.x - horizontalOffset;
      const bypassY = isDownward
        ? Math.max(from.y, to.y) + rowHeight * 0.4
        : Math.min(from.y, to.y) - rowHeight * 0.4;

      return `
        M ${from.x} ${from.y}
        L ${exitX} ${from.y}
        Q ${exitX + cornerRadius} ${from.y} ${exitX + cornerRadius} ${from.y + (bypassY > from.y ? cornerRadius : -cornerRadius)}
        L ${exitX + cornerRadius} ${bypassY + (bypassY > from.y ? -cornerRadius : cornerRadius)}
        Q ${exitX + cornerRadius} ${bypassY} ${exitX} ${bypassY}
        L ${entryX} ${bypassY}
        Q ${entryX - cornerRadius} ${bypassY} ${entryX - cornerRadius} ${bypassY + (to.y > bypassY ? cornerRadius : -cornerRadius)}
        L ${entryX - cornerRadius} ${to.y + (to.y > bypassY ? -cornerRadius : cornerRadius)}
        Q ${entryX - cornerRadius} ${to.y} ${entryX} ${to.y}
        L ${to.x - 6} ${to.y}
      `.replace(/\s+/g, ' ').trim();
    }
  }

  // SS, FF, SF paths
  if (fromSide === 'start' && toSide === 'start') {
    // Start to Start
    const leftX = Math.min(from.x, to.x) - horizontalOffset;

    return `
      M ${from.x} ${from.y}
      L ${leftX + cornerRadius} ${from.y}
      Q ${leftX} ${from.y} ${leftX} ${from.y + (isDownward ? cornerRadius : -cornerRadius)}
      L ${leftX} ${to.y + (isDownward ? -cornerRadius : cornerRadius)}
      Q ${leftX} ${to.y} ${leftX + cornerRadius} ${to.y}
      L ${to.x - 6} ${to.y}
    `.replace(/\s+/g, ' ').trim();
  }

  if (fromSide === 'end' && toSide === 'end') {
    // Finish to Finish
    const rightX = Math.max(from.x, to.x) + horizontalOffset;

    return `
      M ${from.x} ${from.y}
      L ${rightX - cornerRadius} ${from.y}
      Q ${rightX} ${from.y} ${rightX} ${from.y + (isDownward ? cornerRadius : -cornerRadius)}
      L ${rightX} ${to.y + (isDownward ? -cornerRadius : cornerRadius)}
      Q ${rightX} ${to.y} ${rightX - cornerRadius} ${to.y}
      L ${to.x + 6} ${to.y}
    `.replace(/\s+/g, ' ').trim();
  }

  if (fromSide === 'start' && toSide === 'end') {
    // Start to Finish
    const leftX = from.x - horizontalOffset;
    const rightX = to.x + horizontalOffset;

    return `
      M ${from.x} ${from.y}
      L ${leftX + cornerRadius} ${from.y}
      Q ${leftX} ${from.y} ${leftX} ${from.y + (isDownward ? cornerRadius : -cornerRadius)}
      L ${leftX} ${to.y + (isDownward ? -cornerRadius : cornerRadius)}
      Q ${leftX} ${to.y} ${leftX + cornerRadius} ${to.y}
      L ${to.x + 6} ${to.y}
    `.replace(/\s+/g, ' ').trim();
  }

  // Fallback - simple line
  return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
}

/**
 * Calculate arrow head polygon points
 */
function calculateArrowHead(
  point: { x: number; y: number },
  side: 'start' | 'end'
): string {
  const arrowSize = 6;

  if (side === 'start') {
    // Arrow pointing right (to start of task)
    const tipX = point.x;
    const tipY = point.y;
    const baseX = tipX - arrowSize;

    return `${tipX},${tipY} ${baseX},${tipY - arrowSize / 2} ${baseX},${tipY + arrowSize / 2}`;
  } else {
    // Arrow pointing left (to end of task)
    const tipX = point.x;
    const tipY = point.y;
    const baseX = tipX + arrowSize;

    return `${tipX},${tipY} ${baseX},${tipY - arrowSize / 2} ${baseX},${tipY + arrowSize / 2}`;
  }
}

/**
 * Get dependency type string from numeric value
 */
export function getDependencyTypeString(type: number): DependencyType {
  switch (type) {
    case 1:
      return 'SS';
    case 2:
      return 'FS';
    case 3:
      return 'FF';
    case 4:
      return 'SF';
    default:
      return 'FS';
  }
}
